import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { liberacaoService } from '../services';
import { 
  Search,
  Package,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Filter,
  X
} from 'lucide-react';

const SeletorMedicamentos = ({ isOpen, onClose, onSelect, medicamentosSelecionados = [] }) => {
  const [medicamentos, setMedicamentos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('todos');

  useEffect(() => {
    if (isOpen) {
      carregarMedicamentos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, searchTerm, filtroTipo]);

  const carregarMedicamentos = async () => {
    try {
      setLoading(true);
      setError('');
      
      const params = {
        search: searchTerm,
        limit: 100
      };

      const response = await liberacaoService.getMedicamentosEstoque(params);
      
      setMedicamentos(response.medicamentos || []);
      
      if (!response.medicamentos || response.medicamentos.length === 0) {
        setError('Nenhum medicamento encontrado no estoque');
      }
    } catch (error) {
      console.error('Erro ao carregar medicamentos:', error);
      setError(`Erro ao carregar medicamentos do estoque: ${error.message}`);
      setMedicamentos([]);
    } finally {
      setLoading(false);
    }
  };

  const medicamentoJaSelecionado = (medicamentoId) => {
    return medicamentosSelecionados.some(item => 
      item.medicamento_registrado_id === medicamentoId ||
      item.medicamentoRegistradoId === medicamentoId
    );
  };

  // Funções auxiliares precisam ser declaradas antes de serem usadas
  const calcularDiasVencimento = (data) => {
    if (!data) return null;

    const vencimento = new Date(data);
    if (Number.isNaN(vencimento.getTime())) {
      return null;
    }

    const hoje = new Date();
    const diffTime = vencimento - hoje;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
  };

  const formatarDataBR = (data) => {
    if (!data) return 'Não informado';
    const d = new Date(data);
    if (Number.isNaN(d.getTime())) return 'Não informado';
    return d.toLocaleDateString('pt-BR');
  };

  const filtrarMedicamentos = () => {
    let filtrados = medicamentos;

    // Filtrar medicamentos vencidos primeiro
    filtrados = filtrados.filter(med => {
      const proximoVencimento = med.dataValues?.proximoVencimento || med.proximoVencimento;
      if (!proximoVencimento) return true; // Se não tem data, mantém
      
      const diasVenc = calcularDiasVencimento(proximoVencimento);
      return diasVenc === null || diasVenc >= 0; // Remove apenas se estiver vencido (dias < 0)
    });

    // Aplicar filtro de tipo
    if (filtroTipo !== 'todos') {
      filtrados = filtrados.filter(med => {
        const estoqueTotal = parseInt(med.dataValues?.estoqueTotal || med.estoqueTotal || 0);
        const proximoVencimento = med.dataValues?.proximoVencimento || med.proximoVencimento;
        
        switch (filtroTipo) {
          case 'controlado':
            return med.medicamentoRegistrado?.tipoControle && med.medicamentoRegistrado.tipoControle !== 'sem_controle';
          case 'sem_controle':
            return med.medicamentoRegistrado?.tipoControle === 'sem_controle';
          case 'baixo_estoque':
            return estoqueTotal <= 10;
          case 'proximo_vencimento':
            if (!proximoVencimento) return false;
            const diasVenc = calcularDiasVencimento(proximoVencimento);
            return diasVenc !== null && diasVenc >= 0 && diasVenc <= 30;
          default:
            return true;
        }
      });
    }

    return filtrados;
  };

  const medicamentosFiltrados = filtrarMedicamentos();

  const selecionarMedicamento = (medicamento) => {
    const medicamentoId = medicamento.medicamento_registrado_id || medicamento.medicamentoRegistradoId;
    if (medicamentoJaSelecionado(medicamentoId)) {
      return;
    }

    // Criar objeto vinculação fake para compatibilidade
    const vinculacaoFake = {
      id: `estoque_${medicamentoId}`,
      medicamento_registrado_id: medicamentoId,
      medicamentoRegistradoId: medicamentoId,
      medicamentoRegistrado: medicamento.medicamentoRegistrado,
      quantidade_mensal: null, // Medicamento não vinculado - sempre começar com 1
      dosagem_prescrita: medicamento.medicamentoRegistrado.dosagem,
      observacoes: 'Selecionado da lista geral do estoque'
    };

    onSelect(vinculacaoFake);
  };

  const getTipoControleLabel = (tipo) => {
    switch (tipo) {
      case 'sem_controle': return 'Sem Controle';
      case 'controlado': return 'Controlado';
      case 'especial': return 'Especial';
      default: return tipo;
    }
  };

  const getTipoControleCor = (tipo) => {
    switch (tipo) {
      case 'sem_controle': return '#28a745';
      case 'controlado': return '#ffc107';
      case 'especial': return '#dc3545';
      default: return '#6c757d';
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="large">
      <div style={{ 
        width: '100%',
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{ 
          padding: '20px',
          borderBottom: '1px solid #eee',
          flexShrink: 0
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <Package size={24} color="#007bff" />
            <div>
              <h4 style={{ margin: 0, fontSize: '20px' }}>Selecionar Medicamento</h4>
              <p style={{ margin: '4px 0 0 0', color: '#667', fontSize: '14px' }}>
                Escolha um medicamento disponível no estoque
              </p>
            </div>
          </div>

          {/* Busca e Filtros */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
            <div style={{ 
              position: 'relative', 
              flex: 1, 
              minWidth: '250px'
            }}>
              <Search 
                size={18} 
                style={{ 
                  position: 'absolute', 
                  left: '12px', 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  color: '#667'
                }} 
              />
              <input
                type="text"
                placeholder="Buscar por nome ou princípio ativo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px 10px 40px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#667',
                    padding: '4px'
                  }}
                >
                  <X size={16} />
                </button>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Filter size={16} color="#667" />
              <select
                value={filtroTipo}
                onChange={(e) => setFiltroTipo(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px',
                  minWidth: '150px'
                }}
              >
                <option value="todos">Todos</option>
                <option value="controlado">Controlados</option>
                <option value="sem_controle">Sem Controle</option>
                <option value="baixo_estoque">Baixo Estoque</option>
                <option value="proximo_vencimento">Próximo Vencimento</option>
              </select>
            </div>
          </div>
        </div>

        {/* Lista de Medicamentos */}
        <div style={{ 
          flex: 1,
          padding: '20px',
          overflowY: 'auto',
          minHeight: 0
        }}>
          {loading && (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px',
              color: '#667'
            }}>
              Carregando medicamentos disponíveis...
            </div>
          )}

          {error && (
            <div style={{ 
              padding: '20px',
              backgroundColor: '#f8d7da',
              border: '1px solid #f5c6cb',
              borderRadius: '6px',
              color: '#721c24',
              textAlign: 'center'
            }}>
              <AlertTriangle size={20} style={{ marginRight: '8px' }} />
              {error}
            </div>
          )}

          {!loading && !error && medicamentosFiltrados.length > 0 && (
            <div style={{ 
              display: 'grid', 
              gap: '12px',
              gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))'
            }}>
              {medicamentosFiltrados.map((medicamento) => {
                const medicamentoId = medicamento.medicamento_registrado_id || medicamento.medicamentoRegistradoId;
                const jaSelecionado = medicamentoJaSelecionado(medicamentoId);
                const proximoVencimento = medicamento.dataValues?.proximoVencimento || medicamento.proximoVencimento;
                const diasVencimento = calcularDiasVencimento(proximoVencimento);
                const estoqueTotal = parseInt(medicamento.dataValues?.estoqueTotal || medicamento.estoqueTotal || 0);
                const totalLotes = parseInt(medicamento.dataValues?.totalLotes || medicamento.totalLotes || 1);

                return (
                  <div
                    key={medicamentoId}
                    onClick={() => !jaSelecionado ? selecionarMedicamento(medicamento) : null}
                    className="medication-card"
                    style={{
                      border: jaSelecionado ? '2px solid #28a745' : '1px solid #ddd',
                      borderRadius: '8px',
                      padding: '16px',
                      cursor: jaSelecionado ? 'not-allowed' : 'pointer',
                      backgroundColor: jaSelecionado ? '#f8f9fa' : 'white',
                      opacity: jaSelecionado ? 0.7 : 1,
                      transition: 'all 0.2s ease',
                      position: 'relative'
                    }}
                    onMouseEnter={(e) => {
                      if (!jaSelecionado && e.target.classList.contains('medication-card')) {
                        e.target.style.backgroundColor = '#f8f9fa';
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!jaSelecionado && e.target.classList.contains('medication-card')) {
                        e.target.style.backgroundColor = 'white';
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = 'none';
                      }
                    }}
                  >
                    {/* Status de seleção */}
                    {jaSelecionado && (
                      <div style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        backgroundColor: '#28a745',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <CheckCircle size={12} />
                        SELECIONADO
                      </div>
                    )}

                    {/* Informações do medicamento */}
                    <div style={{ marginBottom: '12px' }}>
                      <h5 style={{ 
                        margin: '0 0 6px 0',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        color: '#333'
                      }}>
                        {medicamento.medicamentoRegistrado.nome}
                      </h5>
                      
                      <p style={{ 
                        margin: '0 0 8px 0',
                        color: '#667',
                        fontSize: '13px'
                      }}>
                        {medicamento.medicamentoRegistrado.principioAtivo}
                      </p>

                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                        <span style={{
                          backgroundColor: getTipoControleCor(medicamento.medicamentoRegistrado.tipoControle),
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: 'bold'
                        }}>
                          {getTipoControleLabel(medicamento.medicamentoRegistrado.tipoControle)}
                        </span>

                        <span style={{
                          color: '#667',
                          fontSize: '12px'
                        }}>
                          {medicamento.medicamentoRegistrado.dosagem} • {medicamento.medicamentoRegistrado.formaFarmaceutica}
                        </span>
                      </div>
                    </div>

                    {/* Informações do estoque */}
                    <div style={{ 
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '12px',
                      padding: '12px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '6px'
                    }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ 
                          fontSize: '20px',
                          fontWeight: 'bold',
                          color: estoqueTotal > 20 ? '#28a745' : estoqueTotal > 5 ? '#ffc107' : '#dc3545'
                        }}>
                          {estoqueTotal}
                        </div>
                        <div style={{ fontSize: '11px', color: '#667', fontWeight: '500' }}>
                          Total em Estoque
                        </div>
                      </div>

                      <div style={{ textAlign: 'center' }}>
                        <div style={{ 
                          fontSize: '20px',
                          fontWeight: 'bold',
                          color: '#007bff'
                        }}>
                          {totalLotes}
                        </div>
                        <div style={{ fontSize: '11px', color: '#667', fontWeight: '500' }}>
                          Lotes Disponíveis
                        </div>
                      </div>
                    </div>

                    {/* Próximo vencimento */}
                    <div style={{ 
                      marginTop: '8px',
                      padding: '8px',
                      backgroundColor: diasVencimento !== null && diasVencimento <= 30 ? '#fff3cd' : '#d1ecf1',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <Calendar size={14} color={diasVencimento !== null && diasVencimento <= 30 ? '#856404' : '#0c5460'} />
                      <span style={{ 
                        fontSize: '12px',
                        color: diasVencimento !== null && diasVencimento <= 30 ? '#856404' : '#0c5460',
                        fontWeight: '500'
                      }}>
                        Próximo vencimento: {formatarDataBR(proximoVencimento)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {!loading && !error && medicamentosFiltrados.length === 0 && medicamentos.length > 0 && (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px',
              color: '#667'
            }}>
              <Package size={48} color="#ddd" style={{ marginBottom: '16px' }} />
              <h5 style={{ margin: '0 0 8px 0' }}>Nenhum medicamento encontrado</h5>
              <p style={{ margin: 0, fontSize: '14px' }}>
                Tente ajustar os filtros ou termo de busca
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ 
          padding: '16px 20px',
          borderTop: '1px solid #eee',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
          backgroundColor: '#f8f9fa'
        }}>
          <div style={{ fontSize: '14px', color: '#667' }}>
            {!loading && medicamentosFiltrados.length > 0 && (
              `${medicamentosFiltrados.length} medicamento(s) encontrado(s)`
            )}
          </div>
          
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              backgroundColor: 'white',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f8f9fa';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'white';
            }}
          >
            Fechar
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default SeletorMedicamentos;